/**
 * Every place this app can take you, in one order.
 *
 * D-322. Spec: `docs/specs/2026-08-24-navigation.md`.
 *
 * **One array, and every account renders the subset it can reach.** Three
 * hand-authored role lists were the first draft and could not survive the
 * question the owner asked of them: *what about a teacher who also holds some of
 * Tonya's admin capabilities?* D-5 exists so that happens, so the rail must be
 * defined for any combination rather than for three archetypes — otherwise
 * nothing says whether Calendar sits above or below her own classes, and she
 * gets a different rail depending on who wrote the code.
 *
 * **Derived from capabilities *and* person links.** Capabilities alone cannot do
 * it: `RoleDefaults.Parent` is a single capability against several rail items,
 * and Messages has no capability at all. A person link is not a second
 * authorisation model — it answers *who this account stands for* where a
 * capability answers *what it may do*, and D-307 already unions both for scoping.
 */

export type NavGroup = 'main' | 'money' | 'setup'

export type Destination = {
  key: string
  label: string
  to: string
  group: NavGroup

  /** Any one of these capabilities is enough. Empty means capabilities do not gate it. */
  needsAny?: string[]

  /** Gated on who the account stands for, rather than on what it may do. */
  needsStaff?: boolean
  needsParent?: boolean

  /** She **is** a dancer here, and the destination is about her own days (D-539). */
  needsDancer?: boolean

  /**
   * Gated on having *days* here - her own classes, or a night she is covering.
   *
   * Tonya holds `TakeAttendance` like every admin and teaches nothing; keying
   * this on the capability put an empty Schedule in front of her every day.
   */
  needsTeaching?: boolean

  /**
   * Gated on being the instructor **of record** on a class.
   *
   * **Narrower than `needsTeaching`, and the audit is why.** Covering one night
   * gives you a day at this studio; it does not give you a class. Served by one
   * flag, a substitute got a *My classes* that was guaranteed empty, because the
   * endpoint behind it reads `ClassInstructors` and nothing else - the very
   * defect this gate exists to remove, one destination over.
   */
  needsOwnClasses?: boolean

  /**
   * Held back from a reader who already has the wider thing (D-560).
   *
   * **A predicate rather than a list of capabilities, and an audit is why.**
   * The first version read `notWhenAny: ['ViewAllSchedules']`, which is true
   * of the *teaching* half - the master calendar is her own classes plus
   * everybody else's, filterable by teacher - and false of the other two.
   * It took Schedule from an admin who is also a parent, and with it her
   * children's week, her *report an absence* door, and the landing place of
   * every bell row about a cancelled class: the master calendar has no
   * *my children* view and no per-dancer address. A link that refuses the
   * person holding it, built by the gate meant to remove one.
   *
   * So the condition is written where it can say which relationship it
   * replaces.
   */
  notWhen?: (subject: NavSubject) => boolean

  /**
   * Whether the screen behind it exists yet.
   *
   * **The order is decided now; the item appears when its module lands.** That
   * is the whole reason unbuilt destinations are listed at all — choosing where
   * Hours sits is a decision worth making once, in front of everybody, rather
   * than during M2 when the rail is already somebody's muscle memory.
   *
   * Rendering one before it exists would be a nav item that 404s, which is the
   * "control that lies" problem D-204 and D-261 already paid for.
   */
  built?: boolean

  /**
   * This destination goes away when the named one arrives.
   *
   * **The absorption is data, not a comment.** News folds into Messages when M3
   * lands, and stating that in prose is exactly what let the planned count read
   * six — the limit check could see both and had no way to know one replaces the
   * other. A decision the invariant cannot see is a decision the invariant
   * cannot hold you to.
   */
  supersededBy?: string

  /**
   * Built, but switched off on this host (D-625).
   *
   * **Different from `built`, and the difference is who decides.** `built`
   * says the code does not exist yet and is the same answer everywhere;
   * this says the code exists and somebody has not turned it on here, so QA
   * can have it while production does not.
   *
   * **Read by {@link navItemsFor} and the route guard, never by
   * {@link plannedItemsFor}.** The planned list exists so the rail limit is
   * checked *before* a module lands; a gate reaching it would answer "not
   * there" for ever and the check would pass by being blind, which is the
   * exact failure `supersededBy` was made data to avoid.
   */
  needsModule?: keyof NavSubject['modules']
}

/**
 * The canonical order.
 *
 * **Position here is the decision.** Adding a destination means choosing where
 * it sits once, for everybody — which is the point, and is what stops a granted
 * capability shuffling somebody's rail underneath her.
 *
 * Items marked with a module are not built yet; they are listed so the order is
 * decided before the module arrives rather than during it.
 */
export const ALL_DESTINATIONS: readonly Destination[] = [
  { key: 'home', label: 'Home', to: '/', group: 'main' },

  /*
   * ⚠ **What needs her has no rail row of its own, deliberately** (D-877,
   * D-878, D-884). `asked` 2026-10-09, on a screenshot of the rail carrying
   * both: *"it doesn't need its own tab called What needs you, it should just
   * be the Home page"* — and it already **is** her home, because `landingFor`
   * sends whoever runs the studio there. Two rows for one screen made *Home*
   * the odd one that went somewhere else.
   *
   * `navCurrent.ts`'s `destinationAt` counts `/dashboard` as the home destination, beside
   * `/` and `/home`, so the row she is on is the row that lights.
   */

  // The whole studio's timetable. A teacher reaches her own through `schedule`.
  { key: 'calendar', label: 'Calendar', to: '/calendar', group: 'main', needsAny: ['ViewAllSchedules'] },

  /*
   * <b>The clash list, on the rail rather than inside the calendar</b> (D-585).
   *
   * `asked`: *"its kind of confusing that we have 17 next to calendar cause the
   * conflicts now carries that 17. Should we have it its own view entirely?"*
   *
   * <b>The badge was on the wrong noun.</b> <i>Calendar 17</i> says the
   * calendar needs attention; it does not, the clashes do. The two-number
   * pattern this project documents - a destination badge saying *something in
   * here needs you*, a view count saying *which of these* - earns its keep on
   * Attendance, where one badge covers two views and the number genuinely
   * splits. On the calendar a single view owned the whole figure, so the badge
   * and the tab printed one fact twice.
   *
   * <b>And it is somewhere you go on purpose</b>, which is the reason D-560
   * moved Requests off a button on this same screen. A worklist with a queue is
   * not a way of looking at a date range.
   *
   * <b>It amends D-560's other half</b>, which called this "a view of the
   * calendar rather than a button beside Week" - that decision was choosing
   * between a button and a tab, the rail was not on the table, and the
   * duplicated badge is evidence it did not have.
   */
  { key: 'conflicts', label: 'Conflicts', to: '/conflicts', group: 'main', needsAny: ['ViewAllSchedules'] },

  /*
   * Her own days — her classes if she teaches, her children's if she is a
   * parent, and **both, as two named views of this one destination**, for the
   * sixteen who are both (D-307, D-352). They were stacked on one screen until
   * that was measured at 2.6 screens on a phone; the scope this gate opens is
   * unchanged, only how the screen lays it out.
   *
   * **Not Tonya's**, and that is the same defect `classes` already avoided:
   * she is staff, teaches nothing and has no dancer here, so a link-only gate
   * put an empty screen on her rail every day. She reaches the whole studio
   * through Calendar, which is what her Schedule would have been a worse copy
   * of.
   */
  /*
   * **And her own, when the dancer is the one signed in** (D-539).
   *
   * A dancer's login is new, and most dancers are not student teachers — so
   * the ordinary case is `teaches` false and `isParent` false, which reached
   * *nothing*. She would have had an account, a password and an empty app.
   * Ava is the exception rather than the rule here: she is staff as well, so
   * `needsTeaching` already let her in.
   */
  {
    key: 'schedule',
    label: 'Schedule',
    to: '/schedule',
    group: 'main',
    needsTeaching: true,
    needsParent: true,
    needsDancer: true,

    /*
     * `asked`, 2026-09-08: *"Tonya should just never see both calendar and
     * schedule."* Whoever reads the whole studio reads her own teaching days
     * inside it, and the master calendar filters by teacher - so the second
     * entry offered nothing the first did not, under a name suggesting
     * otherwise (D-560).
     *
     * **Only when teaching is the whole of her claim on it.** Her children and
     * her own dancing are not on the master calendar at all, so a parent or a
     * dancer who is also an admin keeps this - which is the half the first
     * version got wrong.
     */
    notWhen: (s) =>
      s.capabilities.includes('ViewAllSchedules') && !s.isParent && !s.isDancer,
  },

  /*
   * Messages (M3), built behind a switch rather than not built at all (D-625).
   *
   * `needsModule` rather than `built: false`, because the code is here and the
   * host decides: on in QA from the first slice, off in production until the
   * whole module is done. The two say different things and only one of them
   * can be different on two hosts running the same commit.
   *
   * **Third, straight after Schedule, and that is a decision about a phone
   * bar** (`asked` 2026-09-12: *"messages should be prob front and center after
   * schedule as its a primary thing"*).
   *
   * The bar draws four plus *More*, and a teacher reaches six destinations, so
   * position decides who is behind the menu rather than whether anybody is.
   * Putting Messages third takes **My classes** off the bar - `observed` on the
   * rendered bar, which is the only place this is visible, and an earlier
   * comment here got it wrong by counting destinations instead.
   *
   * That trade is the point: a teacher opens her classes when she is planning
   * and messages when somebody is waiting on her, and only one of those is
   * urgent.
   */
  {
    key: 'messages',
    label: 'Messages',
    to: '/messages',
    group: 'main',
    needsStaff: true,
    needsParent: true,
    needsModule: 'messaging',
  },

  /*
   * Requests: where every ask lives, and where a task is assigned (D-560).
   *
   * **A destination now, which amends D-324.** That decision kept requests off
   * the navigation - "not places; things that happened and need somebody to
   * act" - and it was right about a queue of clashes and approvals. The screen
   * changed underneath it: `/asks` is the one home for eight kinds of ask and
   * the only door to *Assign a task*, so it is somewhere you go on purpose
   * rather than a pile of problems. Buried as a button beside Day/Week/Month
   * on the calendar, the owner could not find the feature at all.
   *
   * **Staff only, and a parent is not merely left out - she is served
   * elsewhere.** Her rows are on Home (`asks.home.waitingOn`), and
   * `hrefForSubject` deliberately sends her bell rows to `/` rather than
   * here, with its own comment saying that landing her on this screen would be
   * a link that refuses the person holding it. So a rail entry for every family
   * would be furniture pointing at a screen she is meant not to use - the
   * argument D-324 made, which this does not overturn.
   */
  { key: 'asks', label: 'Requests', to: '/asks', group: 'main', needsStaff: true },

  { key: 'classes', label: 'Classes', to: '/my-classes', group: 'main', needsOwnClasses: true },

  /*
   * News, and it stays (`asked` 2026-09-12, answering the spec's Q9).
   *
   * **This entry used to say Messages absorbed it.** The reasoning was that
   * studio news is an announcement and D-130 already treats closures and posts
   * as one kind of thing mirrored from Facebook — true, and beside the point:
   * News *is* the Facebook feed, and the studio goes on posting publicly where
   * families already follow it while conversations happen privately in the app.
   * Folding them would have taken the feed off the rail on the day this module
   * was switched on, in a commit about something else.
   */
  {
    key: 'news',
    label: 'News',
    to: '/news',
    group: 'main',
    needsStaff: true,
    needsParent: true,
  },

  /*
   * The studio-wide chase-up queue, which is Tonya's — gated on
   * `ManageAttendance` rather than `ViewAttendance`.
   *
   * **Every teacher holds `ViewAttendance`**, so gating on it gave her a
   * second Attendance destination beside the one inside her own classes. Hers
   * is "which of *my* classes still need marking"; this is "which of the
   * studio's do" (D-188, D-118) — genuinely different questions, and the
   * capability that separates them is the one only an admin holds.
   */
  { key: 'attendance', label: 'Attendance', to: '/attendance', group: 'main', needsAny: ['ManageAttendance'] },

  /*
   * The directory, likewise `ManagePeople` rather than `ViewPeople`.
   *
   * A teacher holds `ViewPeople` so she can read a roster; that is not the
   * same as needing a people directory on her rail. She reaches a dancer
   * through search instead - the trade the spec makes for "Find a dancer".
   */
  /*
   * The directory, and **Staff & access is a view inside it**, not its own
   * rail item - owner-confirmed: "people for dancers, families and staff makes
   * sense to me too."
   *
   * So `ManageStaff` opens this destination as surely as `ManagePeople` does,
   * and the views inside gate themselves. Listing only `ManagePeople` would
   * leave somebody granted `ManageStaff` alone (D-5) with no way in at all.
   */
  { key: 'people', label: 'People', to: '/admin/people', group: 'main', needsAny: ['ManagePeople', 'ManageStaff'] },

  /*
   * Her dancer's competition schedule (M10).
   *
   * A genuine destination rather than a view of Schedule: ROADMAP calls it
   * "one of the biggest parent wins in the project", and competitions,
   * entries and venues are new domain concepts rather than a screen bolted
   * onto the calendar.
   */
  {
    key: 'competitions',
    label: 'Competitions',
    to: '/competitions',
    group: 'main',
    needsParent: true,
    built: false,
  },

  // ---- Money — owner-only by default (D-10) ----
  { key: 'hours', label: 'Hours', to: '/hours', group: 'money', needsStaff: true, built: false },
  { key: 'reports', label: 'Reports', to: '/reports', group: 'money', needsAny: ['ViewMoneyOwed'], built: false },

  // ---- Setup ----
  /*
   * <b>Daily work, not season setup</b> (D-583).
   *
   * `asked`: *"is all classes really under Set up? like it is partially setup
   * but its also just viewing and editing the classes."* The group means what
   * is <i>set up once a season and left alone</i> (D-167) - rooms, seasons,
   * the import, the website - and this is not that: the makeup-filing panel
   * lives on it, it is where a class record is opened and edited, and it is
   * where the conflicts screen sends her.
   *
   * <b>It keeps the name.</b> "Classes" is taken by `/my-classes` for anybody
   * who owns classes, so an admin who also teaches would get two entries
   * reading the same word - which is why it was ever called <i>All</i>.
   *
   * <b>Rule 3 is unaffected.</b> The rail cap is five and applies to a base
   * role; this needs `ManageClasses`, which no parent or teacher holds, so it
   * cannot push either over. An owner's rail is uncapped and already at nine in this group (thirteen destinations in all, with messaging on).
   */
  { key: 'class-admin', label: 'All classes', to: '/admin/classes', group: 'main', needsAny: ['ManageClasses'] },
  /*
   * Rooms, seasons, classroom tablets - and **the import is a view inside
   * this one**, which is where D-167 already filed it: the gear is what is set
   * up once a season and left alone. Same reasoning as People and Staff, so
   * `ManageIntegrations` has to open it too.
   */
  /*
   * `ManageClasses`, not `ManageRooms` or `ManageSeasons`. Those two gate
   * nothing behind this door - every route in `StudioSetupEndpoints` asks for
   * `ManageClasses` (its studio-dates read asks only for `ViewSchedule`, which
   * everybody holding this already has) - so for months an account granted
   * either of them by hand was offered a destination that refused it on
   * arrival. `ManageStaff` opens the classroom tablets, `ManageIntegrations`
   * the import and `ManageCarryover` the Jackrabbit updates report — all views
   * inside this one.
   *
   * <b>`ManageCarryover` has to be here even though every Admin already passes
   * this door by role</b> (D-813). It is the one capability of the three that
   * is meant to be granted on its own, so a teacher given it would otherwise
   * hold a working endpoint and a passing route guard with no way in — which
   * is the failure this comment block already exists to prevent.
   */
  {
    key: 'studio',
    label: 'Studio',
    to: '/admin/studio',
    group: 'setup',
    needsAny: ['ManageClasses', 'ManageStaff', 'ManageIntegrations', 'ManageCarryover'],
  },
  // What the pilot has told us (D-607). Owner-only, because it names
  // teachers and describes what is broken.
  { key: 'feedback', label: 'Feedback', to: '/admin/feedback', group: 'setup', needsAny: ['ViewFeedback'] },
  /*
   * What we are telling them (D-790), beside what they told us. Gated on
   * `SendAnnouncements` rather than a capability of its own: a known-issue
   * notice is an announcement, and the owner and admins already hold it.
   */
  { key: 'notices', label: 'Notices', to: '/admin/notices', group: 'setup', needsAny: ['SendAnnouncements'] },
  { key: 'website', label: 'Website', to: '/admin/content', group: 'setup', needsAny: ['EditSiteContent', 'ManageMedia'] },
  { key: 'forms', label: 'Forms', to: '/admin/forms', group: 'setup', needsAny: ['ManageForms'], built: false },
  { key: 'registrations', label: 'Registrations', to: '/admin/registrations', group: 'setup', needsAny: ['ViewRegistrations'], built: false },
]

/** What the rail is built from. Everything here comes off the session. */
export type NavSubject = {
  capabilities: string[]

  /** Person links (D-305, D-307) — who this login stands for. */
  isStaff: boolean
  isParent: boolean

  /**
   * She **is** a dancer here (D-539).
   *
   * **A third relationship, not a variation on the other two.** Ava is staff
   * and a dancer and nobody's parent, so every gate written as
   * `teaches && isParent` gave her the teaching half and no way at all to
   * reach the classes she dances.
   */
  isDancer: boolean

  /** Whether she has days here at all - her own classes, or a night covered. */
  teaches: boolean

  /** Whether she is the instructor of record on a class. Narrower (D-322). */
  ownsClasses: boolean

  /** Opened by PIN on a classroom tablet (D-233). */
  isKiosk: boolean

  /**
   * Which modules this host has switched on (D-625).
   *
   * Comes off the session rather than the build, because the answer differs
   * between QA and production while the code is identical.
   */
  modules: {
    /** `Messaging:Enabled` — whether anybody may hold a conversation here. */
    messaging: boolean
  }
}

/**
 * Five slots on a phone, and the limit the base roles are held to.
 *
 * **`base` is asserted against role defaults, never against a real account.** A
 * granted capability (D-5) *should* add a destination, and a senior teacher
 * holding half of Tonya's grants *should* overflow into `More` — holding more
 * access means having more places to go. What must not happen is a **module**
 * pushing a base role over, because that is a decision somebody should make on
 * purpose rather than discover on a phone.
 */
export function railLimitFor(_who: 'base'): number {
  return 5
}

/**
 * The destinations this account can reach, in canonical order.
 *
 * **The kiosk gets nothing**, and the exclusion lives in `reaches()` rather
 * than here — it was here once, and the route guard, which asks about one
 * destination instead of building a list, never saw it. The rule that travels is
 * *navigation shows what this session can reach*, not *the same bar everywhere*.
 *
 * @param from the destinations to draw from. Defaults to the canonical order;
 * a test overrides it to ask what the rail does **on the day a module lands**,
 * which is the only question the supersession rule exists to answer and is
 * unaskable while every successor is still unbuilt.
 */
export function navItemsFor(
  subject: NavSubject,
  from: readonly Destination[] = ALL_DESTINATIONS,
): Destination[] {
  return withoutSuperseded(
    from.filter(
      (destination) =>
        destination.built !== false &&
        moduleIsOn(subject, destination) &&
        reaches(subject, destination),
    ),
  )
}

/**
 * Whether the module behind a destination is switched on here (D-625).
 *
 * Deliberately not part of {@link reaches}: the router and the rail must agree
 * with each other, and both call this, but `plannedItemsFor` must not — see the
 * field's own note.
 */
function moduleIsOn(subject: NavSubject, destination: Destination): boolean {
  return !destination.needsModule || subject.modules[destination.needsModule]
}

/**
 * What the rail *will* hold, unbuilt modules included.
 *
 * Only the count invariant uses this: the point of asserting a limit is to
 * discover that a module breaks it **before** it is built, which is impossible
 * if the check can only see what already exists.
 */
export function plannedItemsFor(subject: NavSubject): Destination[] {
  return withoutSuperseded(ALL_DESTINATIONS.filter((destination) => reaches(subject, destination)))
}

/**
 * Drop anything the list already contains the successor to.
 *
 * **Both readers share this, and that is the point.** With the rule applied
 * only where the count was checked, the rendered rail would have shown News
 * *and* Messages side by side on the day M3 landed, and the limit test would
 * have reported it as "six is too many" rather than as "one of these replaces
 * the other" - which invites raising the limit instead of deleting the row.
 *
 * Keyed on what is actually in the list, so News survives for an account that
 * cannot reach Messages, and survives everywhere until Messages is built.
 */
function withoutSuperseded(items: Destination[]): Destination[] {
  const present = new Set(items.map((destination) => destination.key))

  return items.filter(
    (destination) => !destination.supersededBy || !present.has(destination.supersededBy),
  )
}

/**
 * Whether this account can reach one named destination.
 *
 * **The router calls this, and so does the rail.** Two implementations of "may
 * she go here" drift, and the drift shows up as a rail item that refuses or a
 * URL that works when nothing offers it - which is worse, because nobody is
 * looking. D-261 paid for this lesson once already with catalog visibility:
 * one function decides, and every screen explains itself from the same answer.
 *
 * An unknown key refuses. A typo in a route guard must not open the route.
 */
export function canReach(subject: NavSubject, key: string): boolean {
  const destination = ALL_DESTINATIONS.find((d) => d.key === key)
  if (!destination) return false

  /*
   * The module gate belongs here as much as on the rail (D-625).
   *
   * **This is the D-233 failure one gate over.** The kiosk exclusion lived in
   * the list functions and this reader never saw it, so a wall tablet could
   * open any screen by URL. A module hidden from the rail and reachable by
   * address is the same mistake: the tab is gone and the page still loads.
   *
   * It cannot move inside `reaches` instead, because `plannedItemsFor` calls
   * that and must keep counting a switched-off module - see the field's note.
   */
  return moduleIsOn(subject, destination) && reaches(subject, destination)
}

function reaches(subject: NavSubject, destination: Destination): boolean {
  /*
   * A kiosk reaches nothing (D-233).
   *
   * **Here rather than in the callers, and a test is why.** It lived in the two
   * list functions, so the route guard - which asks about one destination
   * instead of building a list - never saw it, and a wall tablet could open any
   * screen by URL. D-233 recorded that this boundary fails *open* unless the
   * exclusion is written, and it did so again the first time a third reader
   * appeared. One gate, so the next reader inherits it.
   */
  if (subject.isKiosk) return false

  /*
   * Two gates, and they compose differently on purpose.
   *
   * **Within the relationship gate, any one named relationship is enough.**
   * Schedule means "her own days" to a teacher and "her children's" to a
   * parent, and both at once to the sixteen who are both — reading it as an
   * intersection would give a dual-role account less than either half.
   *
   * **Between the two gates, both must pass.** The teaching check used to
   * return early, so a future destination combining it with a capability would
   * have ignored the capability — an over-grant that reads as correct at the
   * call site.
   */
  if (
    destination.needsTeaching ||
    destination.needsOwnClasses ||
    destination.needsStaff ||
    destination.needsParent ||
    destination.needsDancer
  ) {
    const byRelationship =
      (destination.needsTeaching === true && subject.teaches) ||
      (destination.needsOwnClasses === true && subject.ownsClasses) ||
      (destination.needsStaff === true && subject.isStaff) ||
      (destination.needsParent === true && subject.isParent) ||
      (destination.needsDancer === true && subject.isDancer)

    if (!byRelationship) return false
  }

  // Held back from somebody who already has the wider thing (D-560), before
  // the capability gate, because it is a refusal rather than a grant.
  if (destination.notWhen?.(subject) === true) return false

  if (!destination.needsAny || destination.needsAny.length === 0) return true

  // Any one of them is enough; the views inside a destination gate themselves.
  return destination.needsAny.some((capability) => subject.capabilities.includes(capability))
}
